import React from "react";
import { X, ShieldCheck, AlertTriangle, CheckCircle } from "lucide-react";
import { Badge } from "../../../Component/AdminComponents/Badge";

export default function ProductViewModal({ isOpen, onClose, product }) {
  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Product Details</h2>
            <p className="text-[11px] text-slate-500">
              Complete warranty product information
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 transition"
          >
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">

          {/* Product Overview */}
          <div className="flex gap-4 items-start">
            <img
              src={product.image}
              alt={product.name}
              className="h-20 w-20 rounded-lg object-cover border shadow-sm"
            />

            <div className="flex-1">
              <h3 className="text-base font-bold">{product.name} (HSN/SAC Code: {product.hsn_code})</h3>

              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {product.categories?.map((c) => (
                  <Badge key={c.id} color="blue">
                    {c.name}
                  </Badge>
                ))}
              </div>

              <div className="mt-2">
                <Badge color={product.status === 1 ? "green" : "red"}>
                  {product.status === 1 ? "Active" : "Inactive"}
                </Badge>
              </div>
              <h1 className="text-base font-semibold"></h1>
            </div>
          </div>

          {/* Meta Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MetaCard label="Validity" value={`${product.validity} Days`} />
            <MetaCard label="Claims" value={product.claims} />
            <MetaCard
              label="MRP"
              value={
                <>
                  {product.is_fixed === 1 && "₹"}
                  {product.mrp}
                  {product.is_percent === 1 && "%"}
                </>
              }
            />
            <MetaCard label="Min Value" value={`₹${product.min_value}`} />
            <MetaCard label="Max Value" value={`₹${product.max_value}`} />
            <MetaCard label="Margin" value={`₹${product.margin}`} />
          </div>

          {/* Features */}
          <InfoSection
            title="Features"
            icon={<CheckCircle className="w-4 h-4 text-green-600" />}
            text={product.features}
            color="green"
          />

          {/* Coverage */}
          <InfoSection
            title="Coverage"
            icon={<ShieldCheck className="w-4 h-4 text-blue-600" />}
            text={product.coverage}
            color="blue"
          />

          {/* Exclusions */}
          <InfoSection
            title="Exclusions"
            icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
            text={product.exclusions}
            color="red"
          />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-md bg-slate-600 text-white text-xs hover:bg-slate-500 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 

/* ================== UI Helpers ================== */

const MetaCard = ({ label, value }) => (
  <div className="bg-slate-50 border rounded-lg p-3">
    <p className="text-[11px] text-slate-500">{label}</p>
    <p className="text-sm font-semibold mt-0.5">{value || "—"}</p>
  </div>
);

const InfoSection = ({ title, text, icon, color }) => (
  <div className="bg-white border rounded-xl p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <h4 className="text-sm font-semibold">{title}</h4>
    </div>

    <div className="flex flex-wrap gap-1.5">
      {text
        ?.split(".")
        ?.filter(Boolean)
        ?.map((item, idx) => (
          <span
            key={idx}
            className={`px-2 py-1 text-xs rounded-full bg-${color}-50 text-${color}-700 border border-${color}-200`}
          >
            {item.trim()}
          </span>
        ))}
    </div>
  </div>
);

